import type { SnackBarConfig } from '../../components/Snackbar';
import React, { createContext, useCallback, useContext, useState } from 'react';
import CustomSnackbar from '../../components/Snackbar';

export type MessageType = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarServiceContextValue {
  showSnackbar: (message: string, type?: MessageType, duration?: number) => void;
  closeSnackbar: () => void;
}

const SnackbarServiceContext = createContext<SnackbarServiceContextValue | undefined>(undefined);

export const SnackbarServiceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>({
    open: false,
    message: '',
    type: 'info',
  });

  const showSnackbar = useCallback((message: string, type: MessageType = 'info', duration?: number) => {
    setSnackbarConfig((prev) => ({
      ...prev,
      open: true,
      message,
      type,
      ...(duration !== undefined && { duration }),
    }));
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbarConfig((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  return (
    <SnackbarServiceContext.Provider
      value={{
        showSnackbar,
        closeSnackbar,
      }}
    >
      {children}
      <CustomSnackbar config={snackbarConfig} setOpen={closeSnackbar} />
    </SnackbarServiceContext.Provider>
  );
};

export function useSnackbar() {
  const context = useContext(SnackbarServiceContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarServiceProvider');
  }
  return context;
}
